import type { ErrorLike } from '@apollo/client';
import { CombinedGraphQLErrors, ServerError, ServerParseError } from '@apollo/client/errors';
import type { GraphQLFormattedError } from 'graphql';

/**
 * Apollo Client 4 no longer wraps failures in one `ApolloError` carrying
 * `graphQLErrors` and `networkError`. A failed operation is an `ErrorLike`: a
 * `CombinedGraphQLErrors` when the server answered with GraphQL errors, and
 * anything else (a `ServerError` for an HTTP failure, a plain `Error` when the
 * request never completed) is transport trouble. These two helpers give the
 * old two-way split back, so call sites keep asking the same questions.
 */

/** The GraphQL errors the server returned, or none when the failure was not one. */
export function graphQLErrorsOf(error: unknown): ReadonlyArray<GraphQLFormattedError> {
  return CombinedGraphQLErrors.is(error) ? error.errors : [];
}

/**
 * The transport-level failure behind an error: an HTTP failure (`ServerError`,
 * `ServerParseError`) or a request that never completed (`fetch` rejects with a
 * `TypeError`). Anything else is left alone, so an error the app itself threw is
 * not reported to the user as a connection problem.
 */
export function networkErrorOf(error: unknown): Error | undefined {
  if (ServerError.is(error) || ServerParseError.is(error)) return error;
  return error instanceof TypeError ? error : undefined;
}

/**
 * The JSON body an HTTP failure carried, when it had one. Apollo Client 3 parsed
 * it onto the error as `result`; Client 4 keeps only the raw `bodyText`.
 */
export function serverErrorBody(
  error: unknown,
): { errors?: Array<{ message?: string }>; data?: Record<string, unknown> } | undefined {
  if (!ServerError.is(error)) return undefined;
  try {
    const parsed: unknown = JSON.parse(error.bodyText);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, never>) : undefined;
  } catch {
    return undefined;
  }
}

/** True for an HTTP failure the server answered with this status. */
export function hasHttpStatus(error: unknown, status: number): boolean {
  return ServerError.is(error) && error.statusCode === status;
}

export type { ErrorLike };
